import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';

interface Profile {
  id: string;
  name: string;
  dob: string;
  createdAt: string;
  generatedIndex?: number;
}

export default function PresentPage() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [resultNumber, setResultNumber] = useState<number | null>(null);
  const [resultLoading, setResultLoading] = useState(false);
  const { t } = useTranslation();
  const tableHeaders = t<TranslationContent['present']['select']['table']>('present.select.table');
  const nextSteps = t<string[]>('present.report.nextStepsItems');
  const getDisplayName = useCallback(
    (profile: Profile) =>
      profile.generatedIndex
        ? t('profiles.generatedName', { index: profile.generatedIndex })
        : profile.name,
    [t]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      const now = new Date().toISOString().slice(0, 10);
      const generated = Array.from({ length: 10 }, (_, index) => ({
        id: `P${(index + 1).toString().padStart(3, '0')}`,
        name: '',
        dob: now,
        createdAt: now,
        generatedIndex: index + 1,
      }));
      setProfiles(generated);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  const filtered = useMemo(() => {
    const keyword = search.toLowerCase();
    return profiles.filter(profile => getDisplayName(profile).toLowerCase().includes(keyword));
  }, [profiles, search, getDisplayName]);

  useEffect(() => {
    if (!selected) {
      setResultNumber(null);
      setResultLoading(false);
      return;
    }
    setResultLoading(true);
    const timeout = setTimeout(() => {
      setResultNumber(Math.floor(Math.random() * 9) + 1);
      setResultLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [selected]);

  if (loading) {
    return (
      <div className="panel" style={{ textAlign: 'center' }}>
        <p className="text-muted">{t('present.loading')}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{t('present.badge')}</span>
          <h2>{t('present.title')}</h2>
          <p className="text-muted">{t('present.description')}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{t('present.availableLabel')}</span>
          <strong>{profiles.length}</strong>
        </div>
      </div>

      <div className="panel" style={{ boxShadow: 'none', marginBottom: '2rem' }}>
        <div className="section-heading" style={{ alignItems: 'center' }}>
          <h3>{t('present.select.title')}</h3>
          <input
            placeholder={t('present.select.searchPlaceholder')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            style={{ maxWidth: 260 }}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>{tableHeaders.id}</th>
              <th>{tableHeaders.name}</th>
              <th>{tableHeaders.dob}</th>
              <th>{tableHeaders.created}</th>
              <th aria-label={t('present.select.actionsLabel')} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(profile => (
              <tr key={profile.id}>
                <td>{profile.id}</td>
                <td>{getDisplayName(profile)}</td>
                <td>{profile.dob}</td>
                <td>{profile.createdAt}</td>
                <td>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => setSelected(profile)}
                  >
                    {t('present.select.button')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="panel" style={{ boxShadow: 'none' }}>
          <div className="section-heading">
            <div>
              <h3>{t('present.report.title', { name: getDisplayName(selected) })}</h3>
              <p className="text-muted">{t('present.report.assessmentId', { id: selected.id })}</p>
            </div>
            <div className="tag-pill">{t('present.report.status')}</div>
          </div>

          {!resultLoading && resultNumber !== null ? (
            <div className="form-grid">
              <div className="metric-card" style={{ margin: 0 }}>
                <span>{t('present.report.resultNumber')}</span>
                <strong>{resultNumber}</strong>
              </div>
              <div className="panel" style={{ boxShadow: 'none' }}>
                <h4>{t('present.report.summaryTitle')}</h4>
                <p className="text-muted">{t('present.report.summaryText')}</p>
              </div>
              <div className="panel" style={{ boxShadow: 'none' }}>
                <h4>{t('present.report.nextStepsTitle')}</h4>
                <ul className="text-muted">
                  {nextSteps.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-muted">{t('present.report.loading')}</p>
          )}
        </div>
      )}
    </div>
  );
}
